import type { ReclassChoice } from '@k-management/shared';
import {
  type CSSProperties,
  type KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Icon } from './Icon.js';

const OTHERS = 'Altre voci';

/** The list: at least this wide, at most this high, this far from the edges of the window (px). */
const LIST_WIDTH = 416;
const LIST_HEIGHT = 384;
const EDGE = 8;

/**
 * Where the list goes (1.79): fixed to the window under the box, or above it
 * when there is no room below, kept inside the window. Fixed, a table that
 * scrolls sideways or a dialog does not cut it.
 */
function listPlace(rect: DOMRect): CSSProperties {
  const width = Math.min(Math.max(rect.width, LIST_WIDTH), window.innerWidth - 2 * EDGE);
  const left = Math.max(EDGE, Math.min(rect.left, window.innerWidth - width - EDGE));
  const below = window.innerHeight - rect.bottom - EDGE;
  const above = rect.top - EDGE;
  const up = below < 240 && above > below;
  const maxHeight = Math.min(LIST_HEIGHT, (up ? above : below) - 4);
  return up
    ? { left, width, maxHeight, bottom: window.innerHeight - rect.top + 3 }
    : { left, width, maxHeight, top: rect.bottom + 3 };
}

/**
 * The choice of an item of the plan Base (08/10/2026): the items grouped by
 * their section, as a tree that opens and closes, and a search that filters
 * across the sections by description or code. Arrows and Enter to choose,
 * Esc to close.
 */
export function ReclassPicker({
  value,
  choices,
  onChange,
  disabled = false,
  placeholder = 'Cerca voce o codice…',
  noneLabel = '— nessuna voce',
  shownWhenEmpty,
}: {
  value: string | null;
  choices: readonly ReclassChoice[];
  onChange: (code: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
  /** The first entry of the list, that takes the value away. */
  noneLabel?: string;
  /** What the box shows without a value of its own (e.g. the item of the invoice, for a line). */
  shownWhenEmpty?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<CSSProperties | null>(null);
  const selected = choices.find((choice) => choice.code === value) ?? null;

  // A click outside closes the list.
  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  // The list follows the box when the page or a table scrolls, or the window changes.
  useLayoutEffect(() => {
    if (!open) return;
    const follow = () => {
      if (box.current) setPlace(listPlace(box.current.getBoundingClientRect()));
    };
    follow();
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    return () => {
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, [open]);

  const needle = search.trim().toLowerCase();
  const groups = useMemo(() => {
    const result: { key: string; title: string; code: string | null; items: ReclassChoice[] }[] =
      [];
    for (const choice of choices) {
      if (needle && !`${choice.code} ${choice.description}`.toLowerCase().includes(needle))
        continue;
      const key = choice.sectionCode ?? OTHERS;
      let group = result.find((item) => item.key === key);
      if (!group) {
        group = { key, title: choice.section ?? OTHERS, code: choice.sectionCode, items: [] };
        result.push(group);
      }
      group.items.push(choice);
    }
    return result;
  }, [choices, needle]);
  // While searching every section is open; otherwise as the user left it.
  const isOpen = (key: string) => Boolean(needle) || !closed.has(key);
  const visible = groups.flatMap((group) => (isOpen(group.key) ? group.items : []));

  const start = () => {
    if (disabled) return;
    setSearch('');
    // Only the section of the chosen item open, the others closed.
    setClosed(
      new Set(
        groups
          .map((group) => group.key)
          .filter((key) => !selected || key !== (selected.sectionCode ?? OTHERS)),
      ),
    );
    setActive(
      Math.max(
        0,
        choices.findIndex((choice) => choice.code === value),
      ),
    );
    setOpen(true);
  };
  const choose = (code: string | null) => {
    onChange(code);
    setOpen(false);
  };
  const keys = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => Math.min(index + 1, visible.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const choice = visible[active];
      if (choice) choose(choice.code);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };
  const toggle = (key: string) => {
    const next = new Set(closed);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setClosed(next);
  };

  return (
    <div className="reclass-picker" ref={box}>
      {open ? (
        <input
          autoFocus
          value={search}
          placeholder={placeholder}
          onChange={(event) => {
            setSearch(event.target.value);
            setActive(0);
          }}
          onKeyDown={keys}
        />
      ) : (
        <button type="button" className="reclass-picker-value" disabled={disabled} onClick={start}>
          {selected ? (
            <>
              <span className="reclass-picker-code">{selected.code}</span> {selected.description}
            </>
          ) : value ? (
            `${value} (non nel piano)`
          ) : (
            <span className="muted">{shownWhenEmpty ?? '—'}</span>
          )}
          <Icon name="down" />
        </button>
      )}
      {open && (
        <div className="reclass-picker-list" role="listbox" style={place ?? undefined}>
          {value && (
            <button
              type="button"
              className="reclass-picker-item muted"
              onClick={() => choose(null)}
            >
              {noneLabel}
            </button>
          )}
          {groups.length === 0 && <p className="muted small">Nessuna voce trovata.</p>}
          {groups.map((group) => (
            <div key={group.key}>
              <button
                type="button"
                className="reclass-picker-group"
                onClick={() => !needle && toggle(group.key)}
              >
                <span>
                  {needle ? '' : isOpen(group.key) ? '▾ ' : '▸ '}
                  {group.title}
                </span>
                {group.code && <span className="reclass-picker-code">{group.code}</span>}
              </button>
              {isOpen(group.key) &&
                group.items.map((choice) => {
                  const index = visible.indexOf(choice);
                  return (
                    <button
                      key={choice.code}
                      type="button"
                      role="option"
                      aria-selected={choice.code === value}
                      className={[
                        'reclass-picker-item',
                        index === active ? 'active' : '',
                        choice.code === value ? 'chosen' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => choose(choice.code)}
                    >
                      <span className="reclass-picker-code">{choice.code}</span>
                      {choice.description}
                    </button>
                  );
                })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
